import { useState, useEffect } from 'react'
import { CAMPAIGN_TYPE_OPTIONS } from '../constants/campaignTypes'

interface FilterOption {
  value: string
  label: string
}

interface FilterBarProps {
  onDateRangeChange?: (startDate: string, endDate: string) => void
  onSourceChange?: (source: string) => void
  onTypeChange?: (type: string) => void
  onFlowChange?: (flow: string) => void
  onFiltersChange?: (filters: {
    startDate: string
    endDate: string
    source: string
    type: string
    flow: string
  }) => void
  loading?: boolean
  initialStartDate?: string
  initialEndDate?: string
  initialSource?: string
  initialType?: string
  initialFlow?: string
}

const FilterBar = ({ 
  onFiltersChange,
  loading = false,
  initialStartDate = '2025-09-01',
  initialEndDate = '2025-11-30',
  initialSource = 'all',
  initialType = 'all',
  initialFlow = 'all'
}: FilterBarProps) => {
  const [startDate, setStartDate] = useState(initialStartDate)
  const [endDate, setEndDate] = useState(initialEndDate)
  const [selectedSource, setSelectedSource] = useState(initialSource)
  const [selectedType, setSelectedType] = useState(initialType)
  const [selectedFlow, setSelectedFlow] = useState(initialFlow)

  // Sincronizar con props iniciales cuando cambien
  useEffect(() => {
    setStartDate(initialStartDate)
    setEndDate(initialEndDate)
    setSelectedSource(initialSource)
    setSelectedType(initialType)
    setSelectedFlow(initialFlow)
  }, [initialStartDate, initialEndDate, initialSource, initialType, initialFlow])

  const sources: FilterOption[] = [
    { value: 'all', label: 'Todas las fuentes' },
    { value: 'google', label: 'Google' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'microsoft', label: 'Microsoft' },
    { value: 'tiktok', label: 'TikTok' },
    { value: 'leadcentre', label: 'Leadcentre' },
  ]

  const types: FilterOption[] = CAMPAIGN_TYPE_OPTIONS

  const flows: FilterOption[] = [
    { value: 'all', label: 'Todos los flows' },
    { value: 'seguro-salud', label: 'Seguro Salud' },
    { value: 'creditos-personales', label: 'Créditos Personales' },
    { value: 'tarjeta-credito', label: 'Tarjeta de Crédito' },
    { value: 'seguro-decesos', label: 'Seguro Decesos' },
  ]

  const resetAllFilters = () => {
    console.log('Reseteando todos los filtros')
    setStartDate('2025-09-01')
    setEndDate('2025-11-30')
    setSelectedSource('all')
    setSelectedType('all')
    setSelectedFlow('all')
    
    // Notificar cambios al componente padre
    if (onFiltersChange) {
      onFiltersChange({
        startDate: '2025-09-01',
        endDate: '2025-11-30',
        source: 'all',
        type: 'all',
        flow: 'all'
      })
    }
    
  }

  const hasActiveFilters = selectedSource !== 'all' || selectedType !== 'all' || selectedFlow !== 'all'

  const handleSourceChange = (value: string) => {
    console.log('Seleccionando fuente:', value)
    setSelectedSource(value)
    
    // Notificar cambios al componente padre
    if (onFiltersChange) {
      onFiltersChange({
        startDate,
        endDate,
        source: value,
        type: selectedType,
        flow: selectedFlow
      })
    }
  }

  const handleTypeChange = (value: string) => {
    console.log('Seleccionando tipo:', value)
    setSelectedType(value)
    
    // Notificar cambios al componente padre
    if (onFiltersChange) {
      onFiltersChange({
        startDate,
        endDate,
        source: selectedSource,
        type: value,
        flow: selectedFlow
      })
    }
  }

  const handleFlowChange = (value: string) => {
    console.log('Seleccionando flow:', value)
    setSelectedFlow(value)
    
    // Notificar cambios al componente padre
    if (onFiltersChange) {
      onFiltersChange({
        startDate,
        endDate,
        source: selectedSource,
        type: selectedType,
        flow: value
      })
    }
  }

  const handleDateChange = (newStartDate?: string, newEndDate?: string) => {
    if (newStartDate) {
      console.log('Seleccionando fecha inicio:', newStartDate)
      setStartDate(newStartDate)
    }
    if (newEndDate) {
      console.log('Seleccionando fecha fin:', newEndDate)
      setEndDate(newEndDate)
    }
    
    // Notificar cambios al componente padre
    if (onFiltersChange) {
      onFiltersChange({
        startDate: newStartDate || startDate,
        endDate: newEndDate || endDate,
        source: selectedSource,
        type: selectedType,
        flow: selectedFlow
      })
    }
  }

  return (
    <div className="filter-container">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-dashboard-text flex items-center">
          Filtros de Análisis
          {loading && (
            <div className="ml-3 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-globo-600"></div>
              <span className="ml-2 text-sm text-dashboard-muted">Aplicando...</span>
            </div>
          )}
        </h3>
        <button 
          onClick={resetAllFilters}
          className={`btn-secondary text-sm ${hasActiveFilters ? 'bg-globo-50 border-globo-300 text-globo-700' : ''}`}
        >
          Resetear {hasActiveFilters && '(' + (
            [selectedSource !== 'all', selectedType !== 'all', selectedFlow !== 'all'].filter(Boolean).length
          ) + ')'}
        </button>
      </div>

      <div className="filter-group">
        {/* Date Range */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-dashboard-text">Período:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              handleDateChange(e.target.value, undefined)
            }}
            className="filter-select"
          />
          <span className="text-dashboard-muted">hasta</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              handleDateChange(undefined, e.target.value)
            }}
            className="filter-select"
          />
        </div>

        {/* Source Filter */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-dashboard-text">Fuente:</label>
          <select
            value={selectedSource}
            onChange={(e) => handleSourceChange(e.target.value)}
            className="filter-select min-w-[150px]"
          >
            {sources.map((source) => (
              <option key={source.value} value={source.value}>
                {source.label}
              </option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-dashboard-text">Tipo:</label>
          <select
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="filter-select min-w-[150px]"
          >
            {types.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Flow Filter */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-dashboard-text">Flow:</label>
          <select
            value={selectedFlow}
            onChange={(e) => handleFlowChange(e.target.value)}
            className="filter-select min-w-[150px]"
          >
            {flows.map((flow) => (
              <option key={flow.value} value={flow.value}>
                {flow.label}
              </option>
            ))}
          </select>
        </div>

      </div>

      {/* Active Filters */}
      <div className="mt-4 flex flex-wrap gap-2">
        {selectedSource !== 'all' && (
          <span className="badge badge-info">
            Fuente: {sources.find(s => s.value === selectedSource)?.label}
            <button 
              onClick={() => handleSourceChange('all')}
              className="ml-1 text-blue-600 hover:text-blue-800"
            >
              ×
            </button>
          </span>
        )}
        {selectedType !== 'all' && (
          <span className="badge badge-premium">
            Tipo: {types.find(t => t.value === selectedType)?.label}
            <button 
              onClick={() => handleTypeChange('all')}
              className="ml-1 text-globo-600 hover:text-globo-800"
            >
              ×
            </button>
          </span>
        )}
        {selectedFlow !== 'all' && (
          <span className="badge badge-warning">
            Flow: {flows.find(f => f.value === selectedFlow)?.label}
            <button 
              onClick={() => handleFlowChange('all')}
              className="ml-1 text-yellow-600 hover:text-yellow-800"
            >
              ×
            </button>
          </span>
        )}
      </div>
    </div>
  )
}

export default FilterBar
